import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, KeyRound, ArrowLeft } from "lucide-react";

const LOGO = process.env.REACT_APP_LOGO_URL;
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "bl@symplicity.fr";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Login({ step = "email" }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => sessionStorage.getItem("cfhtb_login_email") || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [devInfo, setDevInfo] = useState(null);
  const [clics, setClics] = useState(0);
  const [showSecret, setShowSecret] = useState(false);
  const [mdp, setMdp] = useState("");
  const [tsToken, setTsToken] = useState("");
  const tsRef = useRef(null);
  const tsWidget = useRef(null);
  const isCodeStep = step === "code";

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || isCodeStep) return;
    let timer;
    const render = () => {
      if (!tsRef.current || !window.turnstile) return;
      tsWidget.current = window.turnstile.render(tsRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t) => setTsToken(t),
        "expired-callback": () => setTsToken(""),
        "error-callback": () => setTsToken(""),
      });
    };
    if (window.turnstile) render();
    else timer = setInterval(() => { if (window.turnstile) { clearInterval(timer); render(); } }, 100);
    return () => { if (timer) clearInterval(timer); };
  }, [isCodeStep]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (TURNSTILE_SITE_KEY && !tsToken) {
      toast.error("Veuillez valider la vérification anti-robot.");
      return;
    }
    setBusy(true);
    try {
      const res = await api("/auth/request-link", { method: "POST", body: { email, turnstile_token: tsToken } });
      sessionStorage.setItem("cfhtb_login_email", email);
      if (res.mode_demo && res.code_dev) setDevInfo(res);
      toast.success(res.message);
      navigate("/connexion/verification");
    } catch (err) {
      toast.error(err.message);
      if (tsWidget.current && window.turnstile) window.turnstile.reset(tsWidget.current);
      setTsToken("");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api("/auth/verify", { method: "POST", body: { code, email } });
      sessionStorage.removeItem("cfhtb_login_email");
      toast.success("Connexion réussie");
      navigate(res.user?.is_admin ? "/admin" : "/");
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onLogoClick = () => {
    const n = clics + 1;
    setClics(n);
    if (n >= 5) setShowSecret(true);
  };

  const deblocage = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api("/auth/deblocage", {
        method: "POST",
        body: { email: ADMIN_EMAIL, mot_de_passe: mdp },
      });
      toast.success("Connexion administrateur réussie");
      navigate(res.user?.is_admin ? "/admin" : "/");
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cfhtb-bg flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img src={LOGO} alt="CFHTB" className="h-16 mx-auto mb-4 select-none" data-testid="login-logo"
            onClick={onLogoClick} draggable={false} />
          <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-cfhtb-heading">
            Espace adhérent
          </h1>
          <p className="text-sm text-cfhtb-text mt-2">
            Confédération Francophone d'Hypnose et Thérapies Brèves
          </p>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 sm:p-8">
          {!isCodeStep ? (
            <form onSubmit={requestLink} className="space-y-5">
              <div>
                <h2 className="font-serif text-lg font-medium text-cfhtb-heading">Connexion sans mot de passe</h2>
                <p className="text-sm text-cfhtb-text mt-1">
                  Saisissez votre adresse email : nous vous enverrons un lien de connexion valable 15 minutes.
                </p>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-cfhtb-heading mb-1.5">
                  Adresse email
                </label>
                <input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.org"
                  className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary"
                />
              </div>
              {TURNSTILE_SITE_KEY && (
                <div ref={tsRef} data-testid="turnstile-widget" className="flex justify-center" />
              )}
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={busy || (!!TURNSTILE_SITE_KEY && !tsToken)}
                className="w-full inline-flex items-center justify-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60"
              >
                <Mail className="h-4 w-4" />
                {busy ? "Envoi en cours…" : "Recevoir mon lien de connexion"}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-5">
              <div>
                <h2 className="font-serif text-lg font-medium text-cfhtb-heading">Vérifiez votre boîte mail</h2>
                <p className="text-sm text-cfhtb-text mt-1">
                  Si un compte existe pour <b>{email}</b>, vous recevrez un lien de connexion.
                  Vous pouvez aussi saisir le code à 6 chiffres reçu dans le même email.
                </p>
              </div>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-cfhtb-heading mb-1.5">
                  Code de vérification
                </label>
                <input
                  id="code"
                  data-testid="login-otp-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full text-center tracking-[0.5em] text-xl rounded-md border border-slate-300 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary"
                />
              </div>
              {devInfo && (
                <div className="rounded-md bg-cfhtb-bg border border-cfhtb-light/40 p-3 text-xs text-cfhtb-text space-y-1" data-testid="login-dev-info">
                  <p className="font-semibold text-cfhtb-heading">Mode démonstration (email non envoyé)</p>
                  <p>Code : <b data-testid="login-dev-code" className="text-cfhtb-primary">{devInfo.code_dev}</b></p>
                  <p>
                    Lien :{" "}
                    <a data-testid="login-dev-link" href={devInfo.lien_dev} className="text-cfhtb-secondary underline break-all">
                      {devInfo.lien_dev}
                    </a>
                  </p>
                </div>
              )}
              <button
                data-testid="login-verify-button"
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full inline-flex items-center justify-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60"
              >
                <KeyRound className="h-4 w-4" />
                {busy ? "Vérification…" : "Me connecter"}
              </button>
              <Link
                to="/connexion"
                data-testid="login-back-link"
                className="inline-flex items-center gap-1.5 text-sm text-cfhtb-secondary hover:text-cfhtb-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Utiliser une autre adresse
              </Link>
            </form>
          )}
        </div>

        {showSecret && (
          <form onSubmit={deblocage} data-testid="login-secret-form"
            className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 mt-4 space-y-4 animate-fade-in">
            <div>
              <h2 className="font-serif text-lg font-medium text-cfhtb-heading">Accès administrateur</h2>
              <p className="text-xs text-cfhtb-text mt-1">Compte : bl@symplicity.fr</p>
            </div>
            <div>
              <label htmlFor="mdp" className="block text-sm font-medium text-cfhtb-heading mb-1.5">
                Mot de passe
              </label>
              <input
                id="mdp"
                data-testid="login-secret-password"
                type="password"
                required
                autoComplete="current-password"
                value={mdp}
                onChange={(e) => setMdp(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary"
              />
            </div>
            <button
              data-testid="login-secret-submit"
              type="submit"
              disabled={busy || !mdp}
              className="w-full inline-flex items-center justify-center gap-2 bg-cfhtb-heading text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-cfhtb-primary transition-colors disabled:opacity-60"
            >
              <KeyRound className="h-4 w-4" />
              {busy ? "Connexion…" : "Déverrouiller"}
            </button>
          </form>
        )}

        <p className="text-center text-xs text-cfhtb-text/70 mt-6">
          Pas encore membre ? Contactez la CFHTB via{" "}
          <a href="https://cfhtb.org" className="text-cfhtb-secondary underline">cfhtb.org</a>
        </p>
      </div>
    </div>
  );
}
